/**
 * Minimal single-flight latch for cross-portal navigation.
 *
 * A nav item that mints an exchange token must not mint a second one while the
 * first request is still outstanding: the extra token is single-use and
 * immediately discarded, and the two racing redirects land on whichever
 * finishes last.
 *
 * The loading overlay is not sufficient on its own. It blocks pointer events,
 * but Vuetify gives a list item with a click listener `tabindex="0"` and turns
 * Enter into a click, so a focused item can still fire from the keyboard while
 * the overlay is up.
 *
 * Kept dependency-free so it can be unit-tested with the Node built-in test
 * runner, mirroring ./nav-mode.js and ./site-scanner-nav-item.js. The helpers
 * that own the latch pull in Vue and HTTP dependencies and cannot be reached
 * from that runner.
 */

/**
 * Create an independent latch.
 *
 * @returns {{ tryBegin: () => boolean, release: () => void, isInFlight: () => boolean }}
 */
export function createSingleFlight () {
	let inFlight = false
	return {
		/**
		 * Claim the latch. Returns true to the first caller and false to every
		 * caller after it until the latch is released, so the caller can simply
		 * bail on false.
		 */
		tryBegin () {
			if (inFlight) return false
			inFlight = true
			return true
		},
		/** Release the latch. Safe to call when it is already free. */
		release () {
			inFlight = false
		},
		isInFlight () {
			return inFlight
		}
	}
}
